import i18n from 'i18next';

/**
 * Backend Error Message Mapper
 * Maps common English backend error messages to translation keys.
 * This ensures users never see raw English errors from the Convex backend.
 */

const ERROR_MAP = {
  // Auth errors
  "Unauthenticated": "app.errors.unauthenticated",
  "Unauthenticated call to mutation": "app.errors.unauthenticated",
  "Unauthorized": "app.errors.unauthorized",
  "Unauthorized: admin only": "app.errors.admin_only",
  "Not authorized to update this client": "app.errors.not_authorized_update",
  "Not authorized to delete this client": "app.errors.not_authorized_delete",
  "Not authorized to update this invoice": "app.errors.not_authorized_update",
  "Not authorized to delete this invoice": "app.errors.not_authorized_delete",
  "Not authorized": "app.errors.unauthorized",

  // Not found errors
  "Team not found": "app.errors.team_not_found",
  "Group not found": "app.errors.group_not_found",
  "Member not found": "app.errors.member_not_found",
  "Project not found": "app.errors.project_not_found",
  "Invoice not found": "app.errors.invoice_not_found",
  "Activity not found": "app.errors.activity_not_found",
  "Task not found": "app.errors.task_not_found",
  "Report not found": "app.errors.report_not_found",
  "Request not found": "app.errors.request_not_found",
  "Role not found": "app.errors.role_not_found",
  "User not found": "app.errors.user_not_found",

  // Company / team errors
  "Team does not belong to this company": "app.errors.team_not_in_company",
  "Work group does not belong to this company": "app.errors.group_not_in_company",
  "User already in team": "app.errors.user_already_in_team",
  "User already in group": "app.errors.user_already_in_group",
  "Company not found": "app.errors.company_not_found",

  // Commission errors
  "Unauthorized to view other user's commissions": "app.errors.unauthorized_view_commissions",
  "Commission can only be paid on paid invoices": "app.errors.commission_paid_only",
  "Commission already paid": "app.errors.commission_already_paid",
  "Only CFO or higher can pay commissions": "app.errors.cfo_required_pay",
  "Unauthorized to review reports": "app.errors.unauthorized_review_reports",
  "Only CFO or higher can approve invoice requests": "app.errors.cfo_required_approve",
  "Commission rate cannot be negative": "app.errors.commission_negative",

  // Role assignment errors
  "Only founders can assign ownership-level roles": "app.errors.founder_assign_only",
  "The founder account cannot be removed": "app.errors.founder_cannot_remove",
  "Cannot delete the founder account": "app.errors.founder_cannot_remove",
  "Unauthorized to remove this role": "app.errors.unauthorized_remove_role",
  "CSM must be assigned to a country": "app.errors.csm_requires_country",
  "ASM must be assigned to a country": "app.errors.asm_requires_country",
  "ASM must be assigned to a region": "app.errors.asm_requires_region",
  "ASM must report to a CSM": "app.errors.asm_requires_csm",

  // Generic
  "Failed to fetch Stripe data": "app.errors.stripe_fetch_failed",
};

/**
 * Translate a backend error message to a user-friendly localized string.
 * Falls back to the original message if no mapping exists.
 */
export function translateBackendError(t, message) {
  if (!message) return t('app.errors.unknown');

  // Exact match
  if (ERROR_MAP[message]) {
    return t(ERROR_MAP[message]);
  }

  // Partial match for dynamic messages (e.g., "Commission rate 20 exceeds maximum 15 for role salesman")
  for (const [pattern, key] of Object.entries(ERROR_MAP)) {
    if (message.includes(pattern)) {
      return t(key);
    }
  }

  // Fallback: return original if already translated or unknown
  return message;
}

/**
 * Hook-friendly wrapper: pass the error and get a translated string.
 * Usage: showError(getTranslatedError(t, e.message))
 */
export function getTranslatedError(t, error) {
  if (!error) return t('app.errors.unknown');
  const message = typeof error === 'string' ? error : error.message;
  return translateBackendError(t, message);
}
